import { createStore } from "vuex";
import { NotarizationDocuments } from "@ado/notarization-vue-common/src/vuex/stores/notarizationDocuments";
import { humanFileSize, extension } from "../../helpers/file.js";
import {
	deleteDocument
} from "@ado/notarization-vue-common/src/API/vfs.api";
import API from "../../vuex/modules/api.js";
import Authorization from "../../vuex/modules/authorization.js";
import UploadMessages from "./UploadMessages";
import { showSignInDialog } from "../../helpers/signin";
import { submitEvent } from "../../helpers/tracking";
import { createNotarization, latestNotarization } from "@ado/notarization-vue-common/src/API/notarization.api.js";

const common = new NotarizationDocuments(API);

export default createStore({
	modules: {
		...common.modules, Authorization
	},
	state() {
		return {
			...common.state(),
			showExistingDialog: false,
			showRemoveDialog: false,
			acceptedExtensions: new Set(window.Notary.Config.AcceptedExtensions.map(e => e.toLowerCase())),
			maxFileSize: window.Notary.Config.MaxFileSize,
			maxSelectedDocuments: window.Notary.Config.MaxSelectedDocuments,
			uploads: [],
			existingUploads: [],
			uploadStatusEnum: {
				UPLOADING: `uploading`,
				DONE: `done`,
				ERROR: `error`,
				TOO_LARGE: `too_large error`,
				FILE_TYPE: `file_type error`,
				ZERO_BYTE: `zero_byte error`,
				CANCEL: `cancelled`
			},
			uploadTypeEnum: {
				UPLOAD: `upload`,
				MYDOCUMENTS: `mydocuments`,
				LAWDEPOT: `lawdepot`
			},
			documentToRemove: null,
			uploadFileInputId: `uploadFile`,			
		};
	},
	getters: {
		acceptAttribute: state => [...state.acceptedExtensions].join(),
		acceptParagraph: state => [...state.acceptedExtensions].map((e, idx, array) => idx !== array.length - 1 ? e : `or ${e}`).join(`, `),
		canAdd: (_, getters) => getters.convertingCount < 5 && !getters.isAtLimit,
		isAtLimit: (state, getters) => (state.notarizationDocuments.length + getters.uploadingCount + getters.convertingCount) >= state.maxSelectedDocuments,
		isEmpty: state => !state.notarizationDocuments.length,
		uploadingCount: state =>
			state.uploads.filter((_) => _.status === state.uploadStatusEnum.UPLOADING).length,
		convertingCount: state =>
			state.existingUploads.filter((_) => _.status === state.uploadStatusEnum.UPLOADING).length,
		errorCount: state =>
			state.uploads.filter((_) => _.status === state.uploadStatusEnum.ERROR).length + state.uploads.filter((_) => _.status === state.uploadStatusEnum.TOO_LARGE).length + state.uploads.filter((_) => _.status === state.uploadStatusEnum.FILE_TYPE).length + state.uploads.filter((_) => _.status === state.uploadStatusEnum.ZERO_BYTE).length,
	},
	mutations: {
		...common.mutations,
		showExistingDialog(state, value) {
			state.showExistingDialog = value;
		},
		showRemoveDialog(state, value) {
			state.showRemoveDialog = value;
		},
		setNotarizationId(state, id) {
			if (id) {
				let slash = window.location.pathname.substr(-1) !== `/` ? `/` : ``;
				window.history.pushState({}, ``, `${window.location.pathname}${slash}${id}`);
				state.API.notarizationId = id;
			}
		},
		setNotarizationIdIfEmpty(state, id) {
			if (!state.API.notarizationId && id) {
				let slash = window.location.pathname.substr(-1) !== `/` ? `/` : ``;
				window.history.pushState({}, ``, `${window.location.pathname}${slash}${id}`);
				state.API.notarizationId = id;
			}
		},
		documentToRemove(state, document) {
			state.documentToRemove = document;
			document.status = state.uploadStatusEnum.CANCEL;
			document.message = `Removing Item...`;			
		},
		addUpload(state, upload) {
			uploadArrayForType(state, upload.type).unshift(upload);
		},
		updateUpload(state, upload) {
			let target = uploadArrayForType(state, upload.type).find(u => u.id === upload.id);
			if (!(target == null)) {
				target.status = upload.status;
				target.message = upload.message;
				// Clear upload arrays when complete to prevent errors adding more documents
				if (target.status === state.uploadStatusEnum.DONE) {
					let index;
					let arr;
					if (state.existingUploads.length) {
						index = state.existingUploads.indexOf(target);
						arr = state.existingUploads;
					} else {
						index = state.uploads.indexOf(target);
						arr = state.uploads;
					}
					arr.splice(index, 1);
				}
			}
		},
		clearUploads(state, upload) {
			let msgKey = upload.id;
			let arr;
			if (state.existingUploads.length) {
				arr = state.existingUploads;				
			} else {
				arr = state.uploads;
			}
			for (let i in arr) {
				if (arr[i].id === msgKey) {
					let removeMsg = arr.indexOf(arr[i]);
					arr.splice(removeMsg, 1);
				}
			}
		},
		openSignInDialog() {
			showSignInDialog();
		},		
		refreshingDocument(state, payload) {
			const { documentId, failed } = payload;
			let doc = state.notarizationDocuments.find(d => d.id === documentId);
			let status = failed ? state.uploadStatusEnum.ERROR : state.uploadStatusEnum.UPLOADING;
			let message = failed ? `Update failed.` : `Updating Item...`;
			if (doc) {
				doc.status = status;
				doc.message = message;
			}
		}
	},
	actions: {
		...common.actions,
		handleUploadPromise({ state, commit, dispatch }, { promise, upload }) {
			let errorMessage = `Failed.`;
			if (upload.type !== state.uploadTypeEnum.UPLOAD)
				errorMessage += ` We can't notarize this file format.`;
			if (state.notarizationDocuments.length >= 5) 
				errorMessage = `File limit exceeded.`;
			function uploadError() {
				commit(`updateUpload`, {
					id: upload.id,
					type: upload.type,
					status: state.uploadStatusEnum.ERROR,
					message: errorMessage
				});
				const component = upload.type === state.uploadTypeEnum.UPLOAD ? `File Upload` : `Add Existing`;
				dispatch(`submitUploadFailureEvent`, { component, extra: errorMessage });
			}
			promise.then((document) => {
				if (document.StatusCode || document.status) {
					uploadError();
				}
				if (upload.status === `cancelled`) {
					// Wait for document to remove before hiding upload status message
					dispatch(`removeDocument`, document).then(() => {
						commit(`clearUploads`, upload);
					});
				} else {
					let message;
					if (upload.type === state.uploadTypeEnum.UPLOAD) {
						message = humanFileSize(upload.file.size);
					} else if (extension(upload.file.name) === `.pdf` || upload.message === UploadMessages.ADD) {
						message = `File added`;
					} else {
						message = `complete`;
					}
					commit(`updateUpload`, {
						id: upload.id,
						type: upload.type,
						status: state.uploadStatusEnum.DONE,
						message: message
					});
					dispatch(`loadDocuments`);
				}
			}).catch((err) => {
				//TODO: refactor code to use the vuex api module (vuex/modules/api.js) like the rest of the code
				if (err.status && err.status === `403`) {
					window.location.replace(`${window.location.origin}`);
					return;
				}
				if (err.status && (err.status === 422 || err.status === 409) && err.messages) {
					if (Array.isArray(err.messages)) {
						errorMessage = ``;
						for (let i in err.messages) {
							errorMessage += err.messages[i].message;
						}
					} else {
						errorMessage = err.messages;
					}
				}
				uploadError();
			});
		},
		removeDocument({ dispatch, state }, document) {
			return dispatch(`API/delete`, { apiPromise: deleteDocument(document.id, state.API.notarizationId), successAction: `loadDocuments` });
		},
		submitUploadFailureEvent(_, { component, extra }) {
			submitEvent(`Document Selection`, component, `Upload Failure`, extra);
		},
		ensureNotarization({ state, commit }, notarySource = null) {
			if (state.API.notarizationId) {
				return Promise.resolve(state.API.notarizationId);
			}
			else {
				let ensurePromise = createNotarization(notarySource);				
				return ensurePromise.then((id) => {
					commit(`setNotarizationIdIfEmpty`, id);
					return id;
				});
			}
		},
		loadLatestNotarization({ commit }) {
			let loadLatestNotarizationPromise = latestNotarization(1); //'Not Started' session id (1) passed into api call

			return loadLatestNotarizationPromise.then((notarizationId) => {
				if (notarizationId != 0) {
					commit(`setNotarizationId`, notarizationId);
					//dispatch(`loadDocuments`);
				}
				return notarizationId;
			});
		},
		doDocumentRefresh({ dispatch, commit }, notarizationDocumentId) {
			//this will update the UI to showing the item being updated
			commit(`refreshingDocument`, {documentId: notarizationDocumentId, failed: false});
			//Call API to refresh document then re-load those documents
			return dispatch(`refreshExistingDocument`, notarizationDocumentId)
				.then(() => {
					return dispatch(`loadDocuments`);
				})
				.catch(() => {
					commit(`refreshingDocument`, {documentId: notarizationDocumentId, failed: true});
				});
		},
		getDocumentIdIfAlreadyExists({ state }, { dataId, fileId }) {
			if (dataId != null) {
				let doc = state.notarizationDocuments.find(o => o.originalDataId != null && o.originalDataId == dataId);
				if (doc)
					return doc.id;
			}
			if (fileId != null) {
				let doc = state.notarizationDocuments.find(o => o.originalFileId != null && o.originalFileId == fileId);
				if (doc)
					return doc.id;
			}

			return null;
		}	
	}
});

function uploadArrayForType(state, type) {
	switch (type) {
		case state.uploadTypeEnum.UPLOAD:
			return state.uploads;
		case state.uploadTypeEnum.MYDOCUMENTS:
		case state.uploadTypeEnum.LAWDEPOT:
			return state.existingUploads;
		default:
			throw new Error(`Invalid upload type`);
	}
}